import type { Quote } from '@tariff/types';
import { rateIdOf } from '@tariff/pieces';
import { useT } from '../lib/i18n';
import { DARK_INK, money, seriesVar, shortDate, weekday } from '../lib/format';

export function SummaryTiles({ quote, nights, arrival }: {
  quote: Quote | null; nights: number; arrival: number;
}) {
  const t = useT();
  const ok = quote !== null && quote.status !== 'unavailable';
  const perNight = ok && nights > 0 ? quote!.total / nights : null;
  const distinct = ok ? new Set(quote!.lines.map((l) => l.pieceId)).size : 0;

  return (
    <section className="tiles">
      <div className="tile lead">
        <span className="k">{t('quote.total')}</span>
        <span className="v">{ok ? money(quote!.total) : '—'}</span>
        <span className="n">
          {ok
            ? t('quote.stayRange', {
              nights,
              from: shortDate(arrival),
              until: shortDate(arrival + nights),
            })
            : t('quote.noCombination')}
        </span>
      </div>
      <div className="tile">
        <span className="k">{t('quote.perNight')}</span>
        <span className="v">{perNight !== null ? money(perNight) : '—'}</span>
        <span className="n">{t('quote.averaged')}</span>
      </div>
      <div className="tile">
        <span className="k">{t('quote.ratesUsed')}</span>
        <span className="v">{ok ? distinct : '—'}</span>
        <span className="n">{ok ? t('quote.placements', { count: quote!.lines.length }) : '—'}</span>
      </div>
      <div className="tile">
        <span className="k">{t('quote.status')}</span>
        <span className="v" style={{ fontSize: 17, paddingTop: 6 }}>
          <StatusPill quote={quote} />
        </span>
        {/* `reason` and `messages` come out of the engine, which writes them
            once and in one language. Shown as they arrive rather than guessed
            at — see the report note about engine-authored text. */}
        <span className="n">
          {quote?.reason
            ?? (quote?.messages?.length ? quote.messages[0].text : t('quote.noConditions'))}
        </span>
      </div>
    </section>
  );
}

export function StatusPill({ quote }: { quote: Quote | null }) {
  const t = useT();
  if (!quote) return <span className="pill bad">{t('quote.engineError')}</span>;
  if (quote.status === 'bookable') return <span className="pill ok">{t('quote.bookable')}</span>;
  if (quote.status === 'onRequest') return <span className="pill warn">{t('quote.onRequest')}</span>;
  return <span className="pill bad">{t('quote.notAvailable')}</span>;
}

/**
 * The stay as a strip of nights, tiled by the rates the engine chose.
 *
 * This is the one view that makes the algorithm legible: the price is not a
 * rate card lookup, it is the cheapest legal TILING, and a stay routinely uses
 * three or four different rates. Every block carries its own label and amount
 * because identity must never rest on fill colour alone.
 */
/**
 * A quote line, and the rule behind it.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS PANEL IS WHERE THE JUMP BELONGS
 * ---------------------------------------------------------------------------
 *
 * Everything needed to explain a price is already on screen here — which rate
 * priced which nights, and for how much — and none of it was reachable. An
 * operator looking at a number they did not expect could see the rate's LABEL
 * and then had to go and find it by eye in a list of thirty-five, on another
 * tab, with nothing connecting the two.
 *
 * A quote line names a PIECE rather than a rule, and `rateIdOf` is what closes
 * that gap: one authored rate becomes several placements and the id is
 * decorated to say which. See `packages/tariff/src/pieces.ts`.
 *
 * A rate that has since been renamed or deleted is simply not offered as a
 * link. Jumping to a selection that does not exist would leave the operator on
 * another tab with an empty panel and no idea why.
 */
export function NightStrip({ quote, arrival, nights, onExplain, hasRate }: {
  quote: Quote | null;
  arrival: number;
  nights: number;
  /** Open the rule behind a line. Absent leaves everything as plain text. */
  onExplain?: (rateId: string) => void;
  /** Whether the card still has that rate, so a dead link is never offered. */
  hasRate?: (rateId: string) => boolean;
}) {
  const t = useT();
  /** The rate to jump to, or null when there is nothing to jump to. */
  const jumpTo = (pieceId: string): string | null => {
    if (!onExplain) return null;
    const id = rateIdOf(pieceId);
    return hasRate && !hasRate(id) ? null : id;
  };
  if (!quote || quote.lines.length === 0) {
    return (
      <p className="note" style={{ padding: '18px 2px' }}>{t('quote.noAvailability')}</p>
    );
  }

  const order: string[] = [];
  for (const l of quote.lines) if (!order.includes(l.pieceId)) order.push(l.pieceId);

  return (
    <>
      <div className="strip-wrap">
        <div className="strip">
          {quote.lines.map((l, i) => {
            const v = seriesVar(order.indexOf(l.pieceId));
            const to = jumpTo(l.pieceId);
            const inside = (
              <>
                <span className="bn">{l.label}</span>
                <span className="bp">{money(l.price)}</span>
                <span className="bx">{t('quote.nightCount', { count: l.nights })}</span>
              </>
            );
            const title = t('quote.blockTitle', {
              label: l.label,
              from: shortDate(l.from),
              nights: l.nights,
              price: money(l.price),
            });
            const style = { flex: `${l.nights} 1 0`, background: `var(${v})` };
            const cls = `blk${DARK_INK.has(v) ? ' ink-dark' : ''}`;

            // A button only where it leads somewhere. A block that looks
            // clickable and is not is worse than one that never invited it.
            return to === null ? (
              <div key={`${l.pieceId}-${l.from}-${i}`} className={cls} style={style} title={title}>
                {inside}
              </div>
            ) : (
              <button
                type="button"
                key={`${l.pieceId}-${l.from}-${i}`}
                className={`${cls} blk-link`}
                style={style}
                title={`${title} — ${t('quote.explain')}`}
                onClick={() => onExplain!(to)}
              >
                {inside}
              </button>
            );
          })}
        </div>
        <div className="ruler">
          {Array.from({ length: nights }, (_, d) => (
            <div className="rc" key={d}>
              {weekday(arrival + d)[0]}
              {' '}
              {shortDate(arrival + d)}
            </div>
          ))}
        </div>
      </div>

      <div className="legend">
        {order.map((id, i) => {
          const lines = quote.lines.filter((l) => l.pieceId === id);
          const sum = lines.reduce((a, l) => a + l.price, 0);
          const to = jumpTo(id);
          const body = (
            <>
              <span className="sw" style={{ background: `var(${seriesVar(i)})` }} />
              {lines[0].label}
              {' · '}
              <span className="num">{money(sum)}</span>
            </>
          );
          return to === null
            ? <span className="lg" key={id}>{body}</span>
            : (
              <button
                type="button"
                className="lg lg-link"
                key={id}
                title={t('quote.explain')}
                onClick={() => onExplain!(to)}
              >
                {body}
              </button>
            );
        })}
      </div>

      <div className="scroll-x" style={{ marginTop: 18 }}>
        <table>
          <thead>
            <tr>
              <th>{t('quote.columnRate')}</th>
              <th>{t('quote.columnFrom')}</th>
              <th className="r">{t('quote.columnNights')}</th>
              <th className="r">{t('quote.columnAmount')}</th>
            </tr>
          </thead>
          <tbody>
            {quote.lines.map((l, i) => {
              const to = jumpTo(l.pieceId);
              return (
              <tr key={`${l.pieceId}-${l.from}-${i}`}>
                <td>
                  {to === null ? l.label : (
                    <button type="button" className="linkish" onClick={() => onExplain!(to)}>
                      {l.label}
                    </button>
                  )}
                </td>
                <td className="num">{shortDate(l.from)}</td>
                <td className="r">{l.nights}</td>
                <td className="r">{money(l.price)}</td>
              </tr>
              );
            })}
            {/*
              SURCHARGES AND ROUNDING ARE TWO LINES, NOT ONE.
              
              This was one row listing every modifier that fired, by id, with
              the whole difference between subtotal and total beside it. On a
              ported card that read "Surcharges: Pricefactor#114" against an
              amount that was entirely the rounding — a name nobody chose,
              against a number it did not cause. The operator asked what it was
              and how to set it, which is the only sane response.

              A modifier with a factor of 1 changed no price. It is a message
              carrier or a rounding rule, and it does not belong under a heading
              that says money is being added.
            */}
            {(() => {
              const charged = quote.appliedModifiers.filter((m) => m.factor !== 1);
              const multiplied = quote.appliedModifiers
                .reduce((acc, m) => acc * m.factor, quote.subtotal);
              /**
               * Against `accommodationTotal`, not `total`.
               *
               * `total` carries the mandatory extras as well, so measuring the
               * rounding against it books a EUR 150 tourist tax as "rounding".
               * The old single row had the same fault and it never showed,
               * because it was labelled "surcharges" and a fee is at least
               * surcharge-shaped. Naming the line correctly is what made the
               * arithmetic behind it wrong.
               */
              const rounding = quote.accommodationTotal - multiplied;
              const extras = quote.extras.filter((e) => !e.optional);
              return (
                <>
                  {charged.length > 0 && (
                    <tr>
                      <td colSpan={3}>
                        {t('quote.surcharges', { list: charged.map((m) => m.label).join(', ') })}
                      </td>
                      <td className="r">{money(multiplied - quote.subtotal)}</td>
                    </tr>
                  )}
                  {Math.abs(rounding) >= 0.005 && (
                    <tr>
                      <td colSpan={3}>{t('quote.rounding')}</td>
                      <td className="r">{money(rounding)}</td>
                    </tr>
                  )}
                  {/* The fees, which were in the total and on no line of the
                      table. A breakdown whose rows do not reach its total is
                      not a breakdown. */}
                  {extras.map((e) => (
                    <tr key={e.id}>
                      <td colSpan={3}>{e.label}</td>
                      <td className="r">{money(e.total)}</td>
                    </tr>
                  ))}
                </>
              );
            })()}
            <tr>
              <td colSpan={3}><strong>{t('quote.total')}</strong></td>
              <td className="r"><strong>{money(quote.total)}</strong></td>
            </tr>
          </tbody>
        </table>
      </div>
    </>
  );
}
